import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import type { RoofTopI } from 'declarations/dto/roofTops';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import {
  AgeFilterValuesE,
  AgentRoleFilterValuesE,
  AssigneeFilterValuesE,
  ScheduleFilterValuesE,
  TicketsRefinementKeysE,
} from 'declarations/enums/tickets';
import type { DmsScheduleAccountsI } from 'declarations/interfaces/dmsScheduleAccounts';
import type { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import type { TicketRequestSubcategoryI } from 'declarations/interfaces/ticketRequestSubcategories';
import type { NoopT } from 'declarations/types/common';
import { useFetchDmsScheduleAccounts } from 'hooks/api/useDmsScheduleAccounts';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTicketRequestSubcategories } from 'hooks/api/useTicketSubcategories';

type TicketsFiltersDropdownPropsT = {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onToggleFilter: (key: TicketsRefinementKeysE, value: string) => NoopT;
  shouldFiltersReset: number;
};

type FilterSectionKey =
  | 'ticketAge'
  | 'other'
  | 'assignTo'
  | 'categories'
  | 'scheduleAge'
  | 'subcategories'
  | 'scheduleAccounts'
  | 'dealers';

const SECTION_LABELS: Record<FilterSectionKey, string> = {
  ticketAge: 'Ticket age',
  other: 'Other',
  assignTo: 'Assignee',
  categories: 'Categories',
  scheduleAge: 'Schedule age',
  subcategories: 'Subcategories',
  scheduleAccounts: 'Schedule accounts',
  dealers: 'Dealers',
};

export const TicketsFiltersDropdown: FC<TicketsFiltersDropdownPropsT> = ({
  anchorEl,
  isOpen,
  onClose,
  onToggleFilter,
  shouldFiltersReset: _shouldFiltersReset,
}): JSX.Element => {
  const {
    activeTab,
    getSearchParam,
    selectedRoofTop,
    updateSearchParamsArray,
    roofTopsList,
    dealerSelectedRooftopIds,
  } = useTickets();

  /** When Dealer card refines to specific dealers, filter list shows only those. */
  const dealersForFilterList = useMemo((): RoofTopI[] => {
    if (!roofTopsList?.length) return [];
    if (!dealerSelectedRooftopIds || dealerSelectedRooftopIds.trim() === '') {
      return roofTopsList;
    }
    const ids = new Set(
      dealerSelectedRooftopIds
        .split(',')
        .map((id): string => id.trim())
        .filter(Boolean)
    );
    return roofTopsList.filter((r): boolean => ids.has(r.id));
  }, [roofTopsList, dealerSelectedRooftopIds]);

  const [subMenuAnchor, setSubMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeSection, setActiveSection] = useState<FilterSectionKey | null>(null);
  const [dealerSearchQuery, setDealerSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSubMenuAnchor(null);
      setActiveSection(null);
      setDealerSearchQuery('');
    }
  }, [isOpen]);

  const categoriesResult = useFetchTicketRequestCategories(true, selectedRoofTop);
  const subCategoriesResult = useFetchTicketRequestSubcategories({
    isEnabled: true,
    roofTopId: undefined,
    oemFamilyId: undefined,
  });
  const scheduleAccountsResult = useFetchDmsScheduleAccounts({
    isEnabled: true,
    roofTopId: selectedRoofTop,
  });

  const [scheduleCategoryId, setScheduleCategoryId] = useState<number | null>(null);

  useEffect((): void => {
    const scheduleCategory = categoriesResult.data?.find(
      (item: TicketRequestCategoryI): boolean => item.categoryName.toLowerCase() === 'schedule'
    );
    setScheduleCategoryId(scheduleCategory ? scheduleCategory.id : null);
  }, [categoriesResult.data]);

  const selectedCategoryIds = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Category) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map(val => Number(val))
      .filter(id => !isNaN(id));
  }, [getSearchParam]);

  const subCategoryParamRaw = getSearchParam(TicketsRefinementKeysE.SubCategory) || '';

  const selectedSubCategoryIds = useMemo((): number[] => {
    if (subCategoryParamRaw === 'All') return [];
    return subCategoryParamRaw
      .split(',')
      .filter(Boolean)
      .map(val => Number(val))
      .filter(id => !isNaN(id));
  }, [subCategoryParamRaw]);

  const isSubCategoryAllSelected = subCategoryParamRaw === 'All';

  const selectedScheduleAccountIds = useMemo((): string[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.DMSScheduleAccounts) || '';
    return raw.split(',').filter(Boolean);
  }, [getSearchParam]);

  const selectedAssigneeNames = useMemo((): string[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Assignee) || '';
    return raw.split(',').filter(Boolean);
  }, [getSearchParam]);

  const isAssigneeAllSelected = selectedAssigneeNames.includes('All');

  const selectedScheduleAgeValues = useMemo((): string[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Schedule) || '';
    return raw
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  }, [getSearchParam]);

  const allScheduleAgeValues = [
    ScheduleFilterValuesE.LessThan31Days,
    ScheduleFilterValuesE.From31To60Days,
    ScheduleFilterValuesE.From61To90Days,
    ScheduleFilterValuesE.From91To120Days,
    ScheduleFilterValuesE.Over120Days,
  ];

  const isScheduleAgeAllSelected =
    selectedScheduleAgeValues.length === 0 ||
    selectedScheduleAgeValues.length === allScheduleAgeValues.length;

  const handleScheduleAgeAll = (): void => {
    updateSearchParamsArray(TicketsRefinementKeysE.Schedule, null);
  };

  const handleToggleScheduleAge = (value: ScheduleFilterValuesE) => (): void => {
    const currentIndex = selectedScheduleAgeValues.indexOf(value);
    const next = [...selectedScheduleAgeValues];
    if (currentIndex === -1) {
      next.push(value);
    } else {
      next.splice(currentIndex, 1);
    }
    updateSearchParamsArray(TicketsRefinementKeysE.Schedule, next.length > 0 ? next : null);
  };

  const handleToggleCategory = (categoryId: number) => (): void => {
    const currentIndex = selectedCategoryIds.indexOf(categoryId);
    const next = [...selectedCategoryIds];
    if (currentIndex === -1) {
      next.push(categoryId);
    } else {
      next.splice(currentIndex, 1);
    }
    const filtered = scheduleCategoryId ? next.filter(id => id !== scheduleCategoryId) : next;
    updateSearchParamsArray(TicketsRefinementKeysE.Category, filtered);
  };

  const handleSubCategoryAll = (): void => {
    if (isSubCategoryAllSelected) {
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, []);
    } else {
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, ['All']);
    }
  };

  const handleToggleSubCategory = (subCategoryId: number) => (): void => {
    const allIds = subCategoriesResult.data?.map((s): number => s.id) ?? [];
    let next: number[];
    if (isSubCategoryAllSelected) {
      next = allIds.filter((id): boolean => id !== subCategoryId);
    } else {
      const currentIndex = selectedSubCategoryIds.indexOf(subCategoryId);
      next = [...selectedSubCategoryIds];
      if (currentIndex === -1) {
        next.push(subCategoryId);
      } else {
        next.splice(currentIndex, 1);
      }
    }
    if (next.length === 0) {
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, []);
    } else if (next.length === allIds.length) {
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, ['All']);
    } else {
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, next);
    }
  };

  const handleToggleScheduleAccount = (accountId: string) => (): void => {
    const currentIndex = selectedScheduleAccountIds.indexOf(accountId);
    const next = [...selectedScheduleAccountIds];
    if (currentIndex === -1) {
      next.push(accountId);
    } else {
      next.splice(currentIndex, 1);
    }
    updateSearchParamsArray(TicketsRefinementKeysE.DMSScheduleAccounts, next.length ? next : null);
  };

  const handleToggleAssignee = (name: string) => (): void => {
    const currentIndex = selectedAssigneeNames.indexOf(name);
    const next = [...selectedAssigneeNames];
    if (currentIndex === -1) {
      next.push(name);
    } else {
      next.splice(currentIndex, 1);
    }
    updateSearchParamsArray(TicketsRefinementKeysE.Assignee, next);
  };

  const handleToggleAssigneeAll = (): void => {
    if (isAssigneeAllSelected) {
      updateSearchParamsArray(TicketsRefinementKeysE.Assignee, []);
    } else {
      updateSearchParamsArray(TicketsRefinementKeysE.Assignee, ['All']);
    }
  };

  // Dealers filter (mutually exclusive - only one dealer at a time)
  const selectedDealerId = useMemo((): string | null => {
    const raw = getSearchParam(TicketsRefinementKeysE.RoofTopId) || '';
    return raw || null;
  }, [getSearchParam]);

  // "All" means no filter (show all dealers) = empty selection in URL
  const isDealerAllSelected = !selectedDealerId;

  const handleDealerAll = (): void => {
    // Clicking "All" clears the dealer filter to show all
    updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, null);
  };

  const handleSelectDealer = (dealerId: string) => (): void => {
    // If already selected, deselect (show all). Otherwise, select this dealer only.
    if (selectedDealerId === dealerId) {
      updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, null);
    } else {
      updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, [dealerId]);
    }
  };

  const handleSectionClick =
    (section: FilterSectionKey) =>
    (e: React.MouseEvent<HTMLElement>): void => {
      setSubMenuAnchor(e.currentTarget);
      setActiveSection(section);
      if (section !== 'dealers') setDealerSearchQuery('');
    };

  const handleBackFromSubmenu = (): void => {
    setSubMenuAnchor(null);
    setActiveSection(null);
    setDealerSearchQuery('');
  };

  const mainSections: FilterSectionKey[] = useMemo(() => {
    const base: FilterSectionKey[] = ['dealers', 'ticketAge', 'other', 'assignTo'];
    if (activeTab === ActiveTabTypeE.Schedule) {
      base.push('scheduleAge', 'subcategories', 'scheduleAccounts');
    } else {
      base.push('categories');
    }
    return base;
  }, [activeTab]);

  const renderSubmenuContent = (): React.ReactNode => {
    if (!activeSection) return null;

    switch (activeSection) {
      case 'ticketAge':
        return (
          <Box sx={{ px: 1, py: 0.5 }}>
            <MenuItem
              onClick={onToggleFilter(
                TicketsRefinementKeysE.TicketAge,
                AgeFilterValuesE.Under24Hours
              )}
            >
              <ListItemIcon>
                <Checkbox
                  checked={
                    getSearchParam(TicketsRefinementKeysE.TicketAge) ===
                    AgeFilterValuesE.Under24Hours
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Under 24h" />
            </MenuItem>
            <MenuItem
              onClick={onToggleFilter(
                TicketsRefinementKeysE.TicketAge,
                AgeFilterValuesE.Over24Hours
              )}
            >
              <ListItemIcon>
                <Checkbox
                  checked={
                    getSearchParam(TicketsRefinementKeysE.TicketAge) ===
                    AgeFilterValuesE.Over24Hours
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Over 24h" />
            </MenuItem>
            <MenuItem onClick={onToggleFilter(TicketsRefinementKeysE.Past30Days, 'true')}>
              <ListItemIcon>
                <Checkbox checked={getSearchParam(TicketsRefinementKeysE.Past30Days) === 'true'} />
              </ListItemIcon>
              <ListItemText primary="Past 30 Days" />
            </MenuItem>
          </Box>
        );

      case 'other':
        return (
          <Box sx={{ px: 1, py: 0.5 }}>
            <MenuItem onClick={onToggleFilter(TicketsRefinementKeysE.PastMonth, 'true')}>
              <ListItemIcon>
                <Checkbox checked={getSearchParam(TicketsRefinementKeysE.PastMonth) === 'true'} />
              </ListItemIcon>
              <ListItemText primary="Past month" />
            </MenuItem>
            <MenuItem onClick={onToggleFilter(TicketsRefinementKeysE.IsArchived, 'true')}>
              <ListItemIcon>
                <Checkbox checked={getSearchParam(TicketsRefinementKeysE.IsArchived) === 'true'} />
              </ListItemIcon>
              <ListItemText primary="Archived" />
            </MenuItem>
            <MenuItem
              onClick={onToggleFilter(
                TicketsRefinementKeysE.AgentRoofTopRole,
                AgentRoleFilterValuesE.Primary
              )}
            >
              <ListItemIcon>
                <Checkbox
                  checked={
                    getSearchParam(TicketsRefinementKeysE.AgentRoofTopRole) ===
                    AgentRoleFilterValuesE.Primary
                  }
                />
              </ListItemIcon>
              <ListItemText primary="My Primary" />
            </MenuItem>
            <MenuItem
              onClick={onToggleFilter(
                TicketsRefinementKeysE.AgentRoofTopRole,
                AgentRoleFilterValuesE.BackUp
              )}
            >
              <ListItemIcon>
                <Checkbox
                  checked={
                    getSearchParam(TicketsRefinementKeysE.AgentRoofTopRole) ===
                    AgentRoleFilterValuesE.BackUp
                  }
                />
              </ListItemIcon>
              <ListItemText primary="My BackUp" />
            </MenuItem>
          </Box>
        );

      case 'assignTo':
        return (
          <Box sx={{ px: 1, py: 0.5 }}>
            <MenuItem onClick={handleToggleAssigneeAll}>
              <ListItemIcon>
                <Checkbox
                  checked={isAssigneeAllSelected}
                  indeterminate={!isAssigneeAllSelected && selectedAssigneeNames.length > 0}
                />
              </ListItemIcon>
              <ListItemText primary="All" />
            </MenuItem>
            {[
              AssigneeFilterValuesE.Dealer,
              AssigneeFilterValuesE.WarrCloud,
              AssigneeFilterValuesE.OEM,
            ].map(
              (name): JSX.Element => (
                <MenuItem key={name} onClick={handleToggleAssignee(name)}>
                  <ListItemIcon>
                    <Checkbox
                      checked={isAssigneeAllSelected || selectedAssigneeNames.includes(name)}
                    />
                  </ListItemIcon>
                  <ListItemText primary={name} />
                </MenuItem>
              )
            )}
          </Box>
        );

      case 'categories':
        return (
          <Box sx={{ px: 1, py: 0.5 }}>
            {(!selectedRoofTop || categoriesResult.isLoading) && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Select a dealership to enable categories.
              </Typography>
            )}
            {selectedRoofTop &&
              categoriesResult.data
                ?.filter((c: TicketRequestCategoryI): boolean => c.id !== scheduleCategoryId)
                .map(
                  (category: TicketRequestCategoryI): JSX.Element => (
                    <MenuItem key={category.id} onClick={handleToggleCategory(category.id)}>
                      <ListItemIcon>
                        <Checkbox checked={selectedCategoryIds.includes(category.id)} />
                      </ListItemIcon>
                      <ListItemText primary={category.categoryName} />
                    </MenuItem>
                  )
                )}
          </Box>
        );

      case 'scheduleAge': {
        const scheduleAgeOptions = [
          { label: '0-30 days', value: ScheduleFilterValuesE.LessThan31Days },
          { label: '31-60 days', value: ScheduleFilterValuesE.From31To60Days },
          { label: '61-90 days', value: ScheduleFilterValuesE.From61To90Days },
          { label: '91-120 days', value: ScheduleFilterValuesE.From91To120Days },
          { label: '121+ days', value: ScheduleFilterValuesE.Over120Days },
        ];
        return (
          <Box sx={{ px: 1, py: 0.5 }}>
            <MenuItem onClick={handleScheduleAgeAll}>
              <ListItemIcon>
                <Checkbox checked={isScheduleAgeAllSelected} />
              </ListItemIcon>
              <ListItemText primary="All" />
            </MenuItem>
            {scheduleAgeOptions.map(
              ({ label, value }): JSX.Element => (
                <MenuItem key={value} onClick={handleToggleScheduleAge(value)}>
                  <ListItemIcon>
                    <Checkbox checked={selectedScheduleAgeValues.includes(value)} />
                  </ListItemIcon>
                  <ListItemText primary={label} />
                </MenuItem>
              )
            )}
          </Box>
        );
      }

      case 'subcategories':
        return (
          <Box sx={{ px: 1, py: 0.5 }}>
            <MenuItem onClick={handleSubCategoryAll}>
              <ListItemIcon>
                <Checkbox
                  checked={isSubCategoryAllSelected}
                  indeterminate={!isSubCategoryAllSelected && selectedSubCategoryIds.length > 0}
                />
              </ListItemIcon>
              <ListItemText primary="All" />
            </MenuItem>
            {subCategoriesResult.data?.map(
              (sub: TicketRequestSubcategoryI): JSX.Element => (
                <MenuItem key={sub.id} onClick={handleToggleSubCategory(sub.id)}>
                  <ListItemIcon>
                    <Checkbox
                      checked={isSubCategoryAllSelected || selectedSubCategoryIds.includes(sub.id)}
                    />
                  </ListItemIcon>
                  <ListItemText primary={sub.name} />
                </MenuItem>
              )
            )}
          </Box>
        );

      case 'scheduleAccounts':
        return (
          <Box sx={{ px: 1, py: 0.5 }}>
            {(!selectedRoofTop || scheduleAccountsResult.isLoading) && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Select a dealership to enable schedule accounts.
              </Typography>
            )}
            {selectedRoofTop &&
              scheduleAccountsResult.data?.map(
                (acc: DmsScheduleAccountsI): JSX.Element => (
                  <MenuItem key={acc.value} onClick={handleToggleScheduleAccount(acc.value)}>
                    <ListItemIcon>
                      <Checkbox checked={selectedScheduleAccountIds.includes(acc.value)} />
                    </ListItemIcon>
                    <ListItemText primary={acc.name} />
                  </MenuItem>
                )
              )}
          </Box>
        );

      case 'dealers': {
        const dealerSearchLower = dealerSearchQuery.trim().toLowerCase();
        const filteredDealers =
          dealersForFilterList?.filter(
            (d: RoofTopI): boolean =>
              !dealerSearchLower || (d.name ?? '').toLowerCase().includes(dealerSearchLower)
          ) ?? [];
        return (
          <Box sx={{ px: 1, py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextField
              size="small"
              placeholder="Search dealers"
              value={dealerSearchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                setDealerSearchQuery(e.target.value)
              }
              variant="outlined"
              sx={{ mb: 0.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: dealerSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear dealer search"
                      size="small"
                      onClick={(): void => setDealerSearchQuery('')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {!dealersForFilterList?.length && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                No dealers available.
              </Typography>
            )}
            {dealersForFilterList && dealersForFilterList.length > 0 && (
              <>
                <MenuItem onClick={handleDealerAll}>
                  <ListItemIcon>
                    <Radio checked={isDealerAllSelected} />
                  </ListItemIcon>
                  <ListItemText primary="All" />
                </MenuItem>
                {filteredDealers.map(
                  (dealer: RoofTopI): JSX.Element => (
                    <MenuItem key={dealer.id} onClick={handleSelectDealer(dealer.id)}>
                      <ListItemIcon>
                        <Radio checked={selectedDealerId === dealer.id} />
                      </ListItemIcon>
                      <ListItemText primary={dealer.name} />
                    </MenuItem>
                  )
                )}
                {dealerSearchQuery.trim() && filteredDealers.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', py: 1 }}>
                    No dealers match your search.
                  </Typography>
                )}
              </>
            )}
          </Box>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
      sx={{ zIndex: 1500 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box sx={{ display: 'flex', overflow: 'visible' }}>
          <Paper sx={{ width: 225, p: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, px: 1, py: 0.5 }}>Filters</Typography>
            <Divider sx={{ my: 0.5 }} />
            {mainSections.map(
              (section): JSX.Element => (
                <MenuItem
                  key={section}
                  onClick={handleSectionClick(section)}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <ListItemText primary={SECTION_LABELS[section]} />
                  <ListItemIcon sx={{ minWidth: 'auto' }}>
                    <ChevronRightIcon fontSize="small" />
                  </ListItemIcon>
                </MenuItem>
              )
            )}
          </Paper>

          <Popper
            open={Boolean(subMenuAnchor)}
            anchorEl={subMenuAnchor}
            placement="right-start"
            disablePortal
            modifiers={[{ name: 'offset', options: { offset: [0, 0] } }]}
            sx={{ zIndex: 1500 }}
          >
            <Paper
              sx={{
                width: 'max-content',
                minWidth: 180,
                maxHeight: 400,
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 1,
                ml: 0.5,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={handleBackFromSubmenu}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  marginBottom: 0.5,
                  color: 'text.secondary',
                  fontSize: 14,
                  width: '100%',
                  justifyContent: 'flex-start',
                  '&:hover': { backgroundColor: 'action.hover' },
                  borderRadius: 1,
                }}
              >
                <KeyboardBackspaceIcon fontSize="small" />
                <Typography variant="body2">
                  {activeSection ? SECTION_LABELS[activeSection] : ''}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              {renderSubmenuContent()}
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};
